/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import Table, { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { Button, message, Tooltip, Typography } from 'antd'; // Added Button
import { find, uniq } from 'lodash';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import {
  getTenderAttachment,
  getTenderVersionedFile,
  getTenderVersionedFileLocationTwo,
} from '~src/services/api';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  dbFileToTableEntry,
  sortByModified,
  sortByType,
  TableEntry,
} from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { styleFn } from './FileTable.style';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import {
  DbFocusOptions,
  DbTenderFolder,
  DbAttachment,
  DbModuleFile,
  DbFile,
  DbTenderSVFAttachment,
} from '~src/types/db';

import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import {
  FileViewerMargins,
  useFileViewerContext,
} from '~src/contexts/FileToViewContext';
import { formatDate } from '~src/utils/formatDate';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useTendersTenderFolders } from '~src/hooks/api-hooks/useTendersTenderFolders';
import { useTendersTenderAttachments } from '~src/hooks/api-hooks/useTendersTenderAttachments';
import { fileIcons } from '~src/config/fileTypeIcons';
import { useTenderFoldersFoldersId } from '~src/hooks/api-hooks/useTenderFoldersFoldersId';
import { useTenderTenderFoldersVersionedFiles } from '~src/hooks/api-hooks/useTenderTenderFoldersVersionedFiles';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';
import { getIsFilePreviewableByIcon } from './FileTable';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';
import { useSortByRevision } from '~src/hooks/utils/useSortByRevision';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';

export interface TenderDocumentsTableProps extends TableProps<TableEntry> {
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  highlightedRow?: TableEntry;
  suMode?: boolean;
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
  handleRow: Required<TenderDocumentsTableProps>['onRow'];
  onEmptyOverlay?: () => void;
  focusOptions?: DbFocusOptions[];
  parentContainerHeight?: string;
  tenderId: string;
  showDeleted?: boolean;
  currentPermissionLevel: RIGHTS_LEVEL;
  onOpenTenderFolders?: () => void;
  onUploadTrigger?: () => void;
  openSVFTranscribe?: (
    fileToView: DbFile | DbModuleFile | DbTenderSVFAttachment
  ) => void;
  tenderAttachmentSVFStateById?: Record<string, number | undefined>;
}
/** Default TenderDocumentsTable, used with folder navigation */
export const TenderDocumentsTable: React.FC<TenderDocumentsTableProps> = ({
  handleRow,
  setSelectedItems,
  onEmptyOverlay,
  onSelect,
  onSelectAll,
  selectedKeys,
  highlightedRow,
  suMode,
  focusOptions,
  parentContainerHeight,
  tenderId,
  showDeleted,
  currentPermissionLevel,
  onOpenTenderFolders,
  onUploadTrigger,
  // SVF viewer props
  openSVFTranscribe,
  tenderAttachmentSVFStateById,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();

  const projectId = useProjectId();
  const archiveSettings = useArchiveSettings();
  const tagsData = archiveSettings.data?.resTags;
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { fileToView, setFileToView } = useFileViewerContext();
  const { sortByRevision } = useSortByRevision();

  const [lastKnownData, setLastKnownData] = useState<any>();

  const pathSegments = history.location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const penultimateSegment = pathSegments[pathSegments.length - 3];

  const isFinishedWithDocumentsPath =
    pathSegments.includes('documents') && !isNaN(Number(lastSegment));

  const foldersQueryResult = useTenderFoldersFoldersId(
    {
      tender_id: tenderId,
      root_folder_id: isFinishedWithDocumentsPath
        ? penultimateSegment
        : undefined,
      folder_id: isFinishedWithDocumentsPath ? penultimateSegment : undefined,
    },
    {
      enabled:
        isFinishedWithDocumentsPath && penultimateSegment === lastSegment,
    }
  );

  useEffect(() => {
    if (foldersQueryResult.data !== undefined) {
      setLastKnownData(foldersQueryResult);
    } else if (!isFinishedWithDocumentsPath) {
      setLastKnownData(undefined);
    }
  }, [foldersQueryResult.data]);

  const foldersTenderFoldersResult = useTendersTenderFolders(
    { tender_id: tenderId },
    { enabled: !isFinishedWithDocumentsPath }
  );

  // Decide which result to use based on the condition
  const foldersQuery = isFinishedWithDocumentsPath
    ? lastKnownData
    : foldersTenderFoldersResult;

  const currentFolder = useCurrentFolder(foldersQuery?.data);
  const folderId = currentFolder?.id || null;

  const isFirstLevel = !pathSegments[5];

  const attachmentsTenderQuery = useTendersTenderAttachments(
    {
      tender_id: tenderId,
      show_deleted: showDeleted,
    },
    { enabled: isFirstLevel }
  );

  const tenderTenderFoldersVersionedFilesResult = useTenderTenderFoldersVersionedFiles(
    {
      tender_id: tenderId,
      folder_id: lastSegment,
      root_folder_id: penultimateSegment,
    },
    { enabled: isFinishedWithDocumentsPath && !!penultimateSegment }
  );

  const [doGetTenderAttachment] = useMutation(getTenderAttachment, {
    onError: (error) => {
      message.error(intl.formatMessage({ id: 'loading-attachment-error' }));
    },
    onSuccess: (data) => {
      setFileToView(data, { marginTop: FileViewerMargins.TABS });
    },
  });

  const [doGetTenderVersionedFile] = useMutation(getTenderVersionedFile, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'loading-attachment-error' }));
    },
    onSuccess: (data) => {
      if (data) {
        setFileToView(data, { marginTop: FileViewerMargins.TABS });
      }
    },
  });

  const [doGetTenderVersionedFileSec] = useMutation(
    getTenderVersionedFileLocationTwo,
    {
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'loading-attachment-error123' })
        );
      },
      onSuccess: (data) => {
        if (data) {
          setFileToView(data, { marginTop: FileViewerMargins.TABS });
        }
      },
    }
  );

  const searchContext = useContext(SearchContext);

  const foldersSource = useMemo(
    () =>
      (foldersQuery?.data || [])
        .filter((v) => !isFinishedWithDocumentsPath || v.parent_id === folderId)
        .map((folder) =>
          dbTenderToTableEntry(folder, foldersQuery?.data[0]?.id)
        ),
    [foldersQuery?.data, folderId, isFinishedWithDocumentsPath]
  );

  const filesSource = useMemo(() => {
    if (isFinishedWithDocumentsPath)
      return (tenderTenderFoldersVersionedFilesResult.data || []).map((item) =>
        dbFileToTableEntry(item, 'tender-admin-inside')
      );

    return (attachmentsTenderQuery.data || []).map(dbFAttachmentToTableEntry);
  }, [
    attachmentsTenderQuery.data,
    tenderTenderFoldersVersionedFilesResult.data,
    isFinishedWithDocumentsPath,
  ]);

  const filteredFolders = [
    ...foldersSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];
  const filteredFiles = [
    ...filesSource.filter((item) => {
      //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
      //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
      const dynamicIndex = (item.dbFile?.versions || []).findIndex(
        (el) =>
          //provided testing function
          el.version === fileVersionsContext[item.id]?.version &&
          fileVersionsContext[item.id]?.version
      );
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          dynamicIndex > 0
            ? item.dbFile?.versions &&
                item.dbFile?.versions[dynamicIndex] &&
                item.dbFile?.versions[dynamicIndex].version
            : item.dbFile?.versions &&
                item.dbFile?.versions[0] &&
                item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const getBackgroundRow = (record: TableEntry) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';

    if (
      (record.key === highlightedRow?.key && data) ||
      record.disabled ||
      window.sessionStorage.getItem('appState') === 'timeMachine'
    )
      return {
        color: 'black',
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    return {
      color: 'black',
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  const { currentFolderId, rootFolderId } = useTenderParamsFromUrl();

  const getDbTenderAtachment = (record: TableEntry) => {
    const file =
      record.dbAttachment?.files?.[record.dbAttachment?.files.length - 1];

    if (record.dbAttachment?.id && isFirstLevel) {
      // console.log(`preview 1`, record);
      doGetTenderAttachment({
        projectId: String(projectId),
        tenderId: String(tenderId),
        attachmentId: record.dbAttachment.id,
      });
    } else if (record.dbAttachment?.id && file && !isFirstLevel) {
      // console.log(`preview 2`, record);
      doGetTenderVersionedFile({
        projectId: String(projectId),
        tenderId: String(tenderId),
        attachmentId: String(file.id),
        currentFolderId: penultimateSegment,
        rootFolderId: penultimateSegment,
        versionedFileId: record.dbAttachment.id,
      });
    } else if (record.dbFile && !isFirstLevel) {
      // Шукаємо вибрану версію у файлах або версіях, з фолбеком на останню
      const versionId = fileVersionsContext[record.id]?.id;
      const fileDetails =
        record.dbFile.files?.find(
          (f: any) =>
            (f.registration_instance_id || f.file_id || f.id) === versionId
        ) ||
        record.dbFile.versions?.find((v: any) => v.id === versionId) ||
        record.dbFile.files?.[record.dbFile?.files.length - 1] ||
        record.dbFile.versions?.[record.dbFile?.versions.length - 1] ||
        record.dbFile.versions?.[0];

      if (!fileDetails) {
        message.error(intl.formatMessage({ id: 'file-preview-error123' }));
        return;
      }

      // console.log('preview 3', record, [
      //   Number(projectId),
      //   Number(tenderId),
      //   Number(rootFolderId),
      //   Number(currentFolderId),
      //   Number(fileDetails.registration_id),
      //   String(fileDetails.file_id),
      // ]);

      // VERSION 1 | new request
      // SWAGGER: tenders -> registration -> tender_folder_and_version_file -> HEAD | AMAZON get version file url
      doGetTenderVersionedFileSec({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        rootFolderId: Number(rootFolderId), // root_folder_id from URL
        folderId: Number(currentFolderId), // folder_id from URL
        versionedFileId: Number(
          fileDetails.registration_id ||
            fileDetails.versioned_file_id ||
            record.dbFile.id
        ),
        fileGuid: String(
          fileDetails.file_id || fileDetails.file_guid || fileDetails.id
        ),
      });

      //   VERSION 2 | request preview 2 from above:
      // doGetTenderVersionedFile({
      //   projectId: String(projectId),
      //   tenderId: String(tenderId),
      //   rootFolderId: String(penultimateSegment), // root_folder_id from URL
      //   currentFolderId: String(penultimateSegment), // folder_id from URL
      //   versionedFileId: String(fileDetails.registration_id),
      //   attachmentId: String(fileDetails.file_id),
      // });

      // VERSION 3 | request preview 1
      // doGetTenderAttachment({
      //   projectId: String(projectId),
      //   tenderId: String(tenderId),
      //   attachmentId: fileDetails.file_id,
      // });

      // VERSION 4 | straight set up
      // setFileToView(record.dbFile.files[0], {
      //   marginTop: FileViewerMargins.TABS,
      // });
    } else {
      message.error(intl.formatMessage({ id: 'file-preview-error123' }));
    }
  };

  // SVF VIEWER FEATURES
  // ==SVF root attachment helpers
  const getAttachmentSVFState = (record: TableEntry) => {
    const fileGuid = String(record.dbAttachment?.id || '');
    const localState = tenderAttachmentSVFStateById?.[fileGuid];

    return typeof localState === 'number'
      ? localState
      : record.dbAttachment?.extension_svf_model?.state;
  };

  const getAttachmentIsConvertible = (record: TableEntry) =>
    record.dbAttachment?.is_svf_convertible === true;

  const getAttachmentSVFFile = (
    record: TableEntry
  ): DbTenderSVFAttachment | undefined => {
    if (!record.dbAttachment) return undefined;

    return {
      ...(record.dbAttachment as any),
      id: String(record.dbAttachment.id),
      tender_id: Number(tenderId),
      sourceType: 'tender-admin-root-svf',
      title: record.dbAttachment.name || record.dbAttachment.title || '',
      file_name: record.dbAttachment.name || record.dbAttachment.title || '',
      extension_svf_model: {
        ...(record.dbAttachment?.extension_svf_model || {}),
        state: getAttachmentSVFState(record),
      },
    } as DbTenderSVFAttachment;
  };

  // ==SVF nested tender file helpers
  const getNestedTenderFileFallback = (record: TableEntry) => {
    if (!record.dbFile) return null;

    const latestFile =
      record.dbFile.files?.[record.dbFile.files.length - 1] ||
      record.dbFile.versions?.[record.dbFile.versions.length - 1] ||
      record.dbFile.versions?.[0];

    if (!latestFile) return null;

    return {
      id:
        latestFile.registration_instance_id ||
        latestFile.file_id ||
        latestFile.id ||
        record.id,
      name: latestFile.name || record.name || record.dbFile.title || '',
      version:
        latestFile.serial_number ??
        latestFile.version ??
        latestFile.revision ??
        1,
      revision:
        latestFile.serial_number ??
        latestFile.version ??
        latestFile.revision ??
        1,
      updated_at: latestFile.updated_at || record.updatedAt,
      description:
        latestFile.registration_instance_description ??
        latestFile.description ??
        record.dbFile.description ??
        '',
      registration_instance_description:
        latestFile.registration_instance_description ?? '',
      is_svf_convertible:
        latestFile.is_svf_convertible ??
        record.dbFile?.versions?.[0]?.is_svf_convertible ??
        false,
      extension_svf_model:
        latestFile.extension_svf_model ??
        record.dbFile?.versions?.[0]?.extension_svf_model ??
        null,
    };
  };

  const getFileSVFVersion = (record: TableEntry) => {
    if (!record.dbFile) return undefined;

    return (
      fileVersionsContext[record.id] ||
      getNestedTenderFileFallback(record) ||
      undefined
    );
  };

  const getFileSVFState = (record: TableEntry) =>
    getFileSVFVersion(record)?.extension_svf_model?.state;

  const getFileIsConvertible = (record: TableEntry) =>
    getFileSVFVersion(record)?.is_svf_convertible === true;

  const getNestedTenderSVFFile = (record: TableEntry) => {
    if (!record.dbFile) return undefined;

    const currentVersion = getFileSVFVersion(record);
    if (!currentVersion || currentVersion.extension_svf_model?.state !== 7) {
      return undefined;
    }

    return {
      ...record.dbFile,
      sourceType: 'tender-admin-inside-svf',
      tender_id: Number(tenderId),
      root_folder_id: Number(rootFolderId),
      folder_id: Number(currentFolderId),
      versioned_file_id: Number(
        currentVersion.registration_id ||
          currentVersion.versioned_file_id ||
          record.dbFile.id
      ),
      file_guid: String(
        currentVersion.file_id || currentVersion.file_guid || currentVersion.id
      ),
      title: currentVersion.name || record.dbFile.title || record.name || '',
      file_name:
        currentVersion.name || record.dbFile.title || record.name || '',
      extension_svf_model: currentVersion.extension_svf_model || null,
      is_svf_convertible: currentVersion.is_svf_convertible === true,
    };
  };

  // ==SVF display helpers
  const shouldShowAttachmentSVF = (record: TableEntry) => {
    const state = getAttachmentSVFState(record);
    const convertible = getAttachmentIsConvertible(record);

    return (
      !!record.dbAttachment &&
      (convertible || [1, 2, 4, 5, 7].includes(state as number))
    );
  };

  const shouldShowFileSVF = (record: TableEntry) => {
    const state = getFileSVFState(record);

    return !!record.dbFile && state === 7;
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: '',
        width: 30,
        align: 'center',
        dataIndex: 'action',
        render: (type, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) => {
                  e.preventDefault();
                  const customEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(customEvent);
                }}
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 75,
        align: 'center',
        render: (type, record) => {
          const isTenderFolder = record.folder;
          const isPreviewableFormat = getIsFilePreviewableByIcon(type.icon);

          const isRootAttachment = !!record.dbAttachment;
          const isNestedFile = !!record.dbFile;

          const attachmentState = getAttachmentSVFState(record);
          const attachmentConvertible = getAttachmentIsConvertible(record);
          const attachmentFile = isRootAttachment
            ? getAttachmentSVFFile(record)
            : undefined;
          const showAttachmentSVFIcon =
            !isTenderFolder &&
            isRootAttachment &&
            shouldShowAttachmentSVF(record);

          const attachmentSVFTooltip = (() => {
            if (attachmentState === 7) {
              return intl.formatMessage({
                id: 'svf-view-file-window',
              });
            }
            if ([1, 2, 4].includes(attachmentState as number)) {
              return intl.formatMessage({
                id: 'svf-view-file-progress',
              });
            }
            if (attachmentState === 5 || attachmentConvertible) {
              return intl.formatMessage({
                id: 'svf-view-file-convert',
              });
            }
            return intl.formatMessage({ id: 'svf-view-file' });
          })();

          const fileVersion = getFileSVFVersion(record);
          const fileState = getFileSVFState(record);
          const showFileSVFIcon =
            !isTenderFolder && isNestedFile && shouldShowFileSVF(record);

          const fileSVFTooltip = intl.formatMessage({
            id: 'svf-view-file-window',
          });

          const handleAttachmentSVFClick = (e?: any) => {
            e?.stopPropagation?.();

            if (!record.dbAttachment) {
              message.error(intl.formatMessage({ id: 'file-preview-error' }));
              return;
            }

            const ready = attachmentState === 7;

            if (ready) {
              if (attachmentFile) {
                setFileToView(attachmentFile as any, {
                  marginTop: FileViewerMargins.TABS,
                });
              }
              return;
            }

            if (
              openSVFTranscribe &&
              (record.dbAttachment?.is_svf_convertible === true ||
                [1, 2, 4, 5].includes(attachmentState as number))
            ) {
              if (attachmentFile) {
                openSVFTranscribe(attachmentFile as any);
                setFileToView(undefined);
              }
            }
          };

          const handleFileSVFClick = (e?: any) => {
            e?.stopPropagation?.();

            if (!record.dbFile) {
              message.error(intl.formatMessage({ id: 'file-preview-error' }));
              return;
            }

            if (fileState !== 7) {
              return;
            }

            const nestedTenderSVFFile = getNestedTenderSVFFile(record);

            if (!nestedTenderSVFFile) {
              message.error(intl.formatMessage({ id: 'file-preview-error' }));
              return;
            }

            setFileToView(nestedTenderSVFFile as any, {
              marginTop: FileViewerMargins.TABS,
            });
          };

          const ext = record.extension?.toLowerCase() || '';
          const isCadFile = ['rvt', 'dwg', 'ifc'].includes(ext);

          const tableFileIconVersion = isRootAttachment
            ? ({
                extension_svf_model:
                  record.dbAttachment?.extension_svf_model ?? null,
                is_svf_convertible:
                  record.dbAttachment?.is_svf_convertible === true,
                name:
                  record.dbAttachment?.name || record.dbAttachment?.title || '',
              } as any)
            : (fileVersion as any);

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Fragment>
                {isTenderFolder && (
                  <Tooltip
                    placement="bottom"
                    title={intl.formatMessage({
                      id: isFirstLevel ? 'tender-folder' : 'folder',
                    })}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative',
                      }}>
                      <div css={styles.iconContainer}>
                        <NavIcon
                          icon={getTenderIconArichive(record.dbFolder?.option)}
                        />
                      </div>
                    </div>
                  </Tooltip>
                )}

                {!isTenderFolder && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      position: 'relative',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {isPreviewableFormat && (
                        <div css={styles.iconContainer}>
                          <IconButton
                            click={(e) => {
                              e.stopPropagation();
                              getDbTenderAtachment(record);
                            }}
                            iconStyle={type.iconStyle}
                            icon={['far', 'eye']}
                            color="red"
                          />
                        </div>
                      )}

                      {showAttachmentSVFIcon && (
                        <SVFIconType
                          key={`svf-attachment-${record.key}-${
                            attachmentState ?? 'none'
                          }`}
                          version={
                            {
                              extension_svf_model: {
                                ...(record.dbAttachment?.extension_svf_model ||
                                  {}),
                                state: attachmentState,
                              },
                              is_svf_convertible: attachmentConvertible,
                              name:
                                record.dbAttachment?.name ||
                                record.dbAttachment?.title ||
                                '',
                            } as any
                          }
                          extension={record.extension}
                          variant="sidebar"
                          disabled={false}
                          customTooltip={attachmentSVFTooltip}
                          onClick={handleAttachmentSVFClick}
                          type={type}
                        />
                      )}

                      {showFileSVFIcon && (
                        <SVFIconType
                          key={`svf-file-${record.id}-${fileState ?? 'none'}`}
                          version={fileVersion as any}
                          extension={record.extension}
                          variant="sidebar"
                          disabled={false}
                          customTooltip={fileSVFTooltip}
                          onClick={handleFileSVFClick}
                          type={type}
                        />
                      )}

                      {isCadFile ? (
                        <SVFIconType
                          key={`file-icon-${record.key}`}
                          version={tableFileIconVersion}
                          extension={record.extension}
                          variant="table"
                          customTooltip={intl.formatMessage(
                            { id: type.icon },
                            { type: record.extension?.toUpperCase() }
                          )}
                          onClick={() => {
                            // no-op, icon only
                          }}
                          type={type}
                        />
                      ) : (
                        <IconButton
                          iconStyle={type.iconStyle}
                          icon={type.icon}
                          iconSecond={
                            record.state === DB_STATE.REMOVED ||
                            record.state === DB_STATE.DELETED
                              ? ['fad', 'trash']
                              : null
                          }
                          colorSecondIcon="red"
                          customStyleSecondIcon={{
                            left: '12px',
                            top: '10px',
                          }}
                          color={type.color}
                          customStyle={styles.icons}
                          tooltip={intl.formatMessage(
                            { id: type.icon },
                            { type: record.extension?.toUpperCase() }
                          )}
                          badge={
                            record.state !== DB_STATE.REMOVED &&
                            record.state !== DB_STATE.DELETED &&
                            type.is_empty &&
                            '0'
                          }
                          backgroundOff
                        />
                      )}
                    </div>
                  </div>
                )}
              </Fragment>
            ),
          };
        },
        sorter: sortByType,
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 180,
        render: (name, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Fragment>
                {record.dbTenderFolder?.options.alias_title || name}
                {record.favourite && (
                  <IconButton
                    icon={['fas', 'star']}
                    color="yellow"
                    backgroundOff
                  />
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        width: 175,
        ellipsis: true,
        render: (node, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children:
              fileVersionsContext[record.id]
                ?.registration_instance_description ??
              fileVersionsContext[record.id]?.description ??
              record.dbFile?.description ??
              record.description,
          };
        },
      },
      //@ts-ignore
      ...(!isFirstLevel
        ? [
            {
              title: intl.formatMessage({ id: 'ver' }),
              dataIndex: 'dbFile',
              width: 80,
              align: 'center',
              onCell: () => ({ style: { padding: '4px 10px' } }),
              render: (file: any, element: any) => {
                let passFile = file;
                if (
                  file &&
                  (!file.versions || !file.versions.length) &&
                  file.files &&
                  file.files.length
                ) {
                  passFile = {
                    ...file,
                    // Додаємо .sort() в самому кінці
                    versions: file.files
                      .map((f: any) => ({
                        ...f,
                        revision: f.serial_number ?? f.version ?? f.revision,
                        version: f.serial_number ?? f.version ?? f.revision,
                        id: f.registration_instance_id || f.file_id || f.id,
                      }))
                      .sort(
                        (a: any, b: any) =>
                          (b.revision || 0) - (a.revision || 0)
                      ),
                  };
                }
                return {
                  props: {
                    style: getBackgroundRow(element),
                  },
                  children: passFile && <VersionSelect file={passFile} />,
                };
              },
            },
          ]
        : []),
      {
        title: intl.formatMessage({ id: 'modified-date' }),

        dataIndex: 'updatedAt',
        sorter: sortByModified(fileVersionsContext),

        width: 140,
        align: 'center',
        render: (updatedAt, record) => {
          let modified = updatedAt;

          // if file, show "modified" of selected version
          if (record.dbFile?.versions || record.dbFile?.files) {
            modified =
              fileVersionsContext[record.id]?.updated_at ||
              record.dbFile?.files?.[record.dbFile.files.length - 1]
                ?.updated_at ||
              record.dbFile?.versions?.[record.dbFile.versions.length - 1]
                ?.updated_at ||
              record.dbFile?.versions?.[0]?.updated_at ||
              updatedAt;
          }
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: formatDate(modified, 'short'),
          };
        },
      },
    ],
    [
      fileVersionsContext,
      intl,
      styles.icons,
      styles.small,
      focusOptions,
      highlightedRow,
      tagsData,
      isFirstLevel,
      getBackgroundRow,
    ]
  );

  // disable selection (cherrypicker) if no RIGHTS_LEVEL.OPEN on current folder
  const selectionDisabled =
    (isFirstLevel ? RIGHTS_LEVEL.MANAGE : currentPermissionLevel) <
    RIGHTS_LEVEL.OPEN;

  /** calculate !getCheckboxProps.disabled */
  const isRecordSelectDisabled = (record: TableEntry) => {
    // if (record.folder && chosenFileIetms.length > 0) return false;
    if (record.folder) return false;

    if (window.sessionStorage.getItem('appState') === 'superUser') {
      return false;
    }

    // if root, use folder permission
    if (folderId === null && record.dbFolder) {
      return record.dbFolder.permission < RIGHTS_LEVEL.OPEN;
    }
    if (suMode && record.state === DB_STATE.REMOVED) {
      return false;
    }

    return (
      selectionDisabled ||
      (record.state !== DB_STATE.REMOVED && record.state !== DB_STATE.DELETED
        ? record.disabled
        : false)
    );
  };

  const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const getTableData = () => {
    return [...filteredFolders, ...filteredFiles];
  };

  const content = (
    <Fragment>
      <Table<TableEntry>
        //  scroll={{ x: '100%', y: '50%' }} // Add this line
        css={[styles.table]}
        showSorterTooltip={false}
        locale={{
          emptyText: (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                textAlign: 'center',
                gap: '32px',
                maxWidth: '700px',
                margin: '0 auto',
              }}>
              {/* Folders Section */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  alignItems: 'center',
                }}>
                <div css={{ marginBottom: 22 }}>
                  <Typography.Paragraph css={styles.text}>
                    {intl.formatMessage({ id: 'no-documents-found' })}
                  </Typography.Paragraph>
                  <Typography.Text type="secondary">
                    {intl.formatMessage({ id: 'no-documents-help-text' })}
                  </Typography.Text>
                </div>
                {isFirstLevel && (
                  <div>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      {intl.formatMessage({
                        id: 'tender-documents-empty-folders-title',
                      })}
                    </Typography.Title>
                    <Typography.Text
                      type="secondary"
                      style={{ whiteSpace: 'pre-wrap' }}>
                      {intl.formatMessage({
                        id: 'tender-documents-empty-folders-description',
                      })}
                    </Typography.Text>
                    <div style={{ marginTop: '4px' }}>
                      <Button
                        type="primary"
                        ghost
                        onClick={onOpenTenderFolders}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}>
                        <IconButton
                          icon={['far', 'folder-plus']}
                          backgroundOff
                          customStyle={{ marginRight: 8, padding: 0 }}
                        />
                        <span style={{ lineHeight: '1' }}>
                          {intl.formatMessage({
                            id: 'tender-documents-edit-tender-folders-btn',
                          })}
                        </span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Section */}
              {isFirstLevel && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      alignItems: 'center',
                    }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      {intl.formatMessage({
                        id: 'tender-documents-empty-upload-title',
                      })}
                    </Typography.Title>
                    <Typography.Text
                      type="secondary"
                      style={{ whiteSpace: 'pre-wrap' }}>
                      {intl.formatMessage({
                        id: 'tender-documents-empty-upload-description',
                      })}
                    </Typography.Text>
                    <div style={{ marginTop: '4px' }}>
                      <Button
                        type="primary"
                        onClick={onUploadTrigger}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}>
                        <IconButton
                          icon={['far', 'file-upload']}
                          backgroundOff
                          customStyle={{ marginRight: 8, padding: 0 }}
                        />
                        <span style={{ lineHeight: '1' }}>
                          {intl.formatMessage({ id: 'upload-file' })}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ),
        }}
        className={`main-content-window ${
          window.sessionStorage.getItem('appState') === 'timeMachine' &&
          'timeMachine-table'
        } ${suMode && 'su-table'}`}
        pagination={false}
        bordered
        columns={columns}
        dataSource={[...filteredFolders, ...filteredFiles]}
        loading={
          foldersQuery?.isLoading ||
          foldersQueryResult?.isLoading ||
          tenderTenderFoldersVersionedFilesResult?.isLoading ||
          foldersTenderFoldersResult?.isLoading ||
          foldersQuery?.isIdle
        }
        rowClassName={(record) => {
          let className = suMode ? 'su-row-table ' : '';
          if (window.sessionStorage.getItem('appState') === 'timeMachine') {
            className += 'timeMachine-row-table' + ' ';
          }
          if (record.key === highlightedRow?.key) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (
            record.key === highlightedRow?.key &&
            record.dbFolder &&
            !record.dbFolder.focus
          ) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedKeys,
          columnWidth: 50,
          renderCell: (type, record, element, node) => {
            return {
              props: {
                style: getBackgroundRow(record),
              },
              children: node,
            };
          },
          onSelect,
          onSelectAll,
          getCheckboxProps: (record) => ({
            disabled: isRecordSelectDisabled(record),
          }),
        }}
        onRow={(record, index) => ({
          ...handleRow(record, index),
          onClick: (event) => {
            if (event.shiftKey) {
              return handleSelectShift(record, index);
            }
            const onClickAction = handleRow(record, index).onClick;
            if (onClickAction) {
              return onClickAction(event);
            }
          },
        })}
        {...tableProps}
      />
      <div
        onContextMenu={() => {
          onEmptyOverlay && onEmptyOverlay();
        }}
        className="test-class"
        style={{ height: 'inherit', width: '100%' }}></div>
    </Fragment>
  );

  if (fileToView) {
    return (
      <div
        style={{
          height: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          display: 'flex',
          flexDirection: 'column',
          width: '1230px',
        }}>
        {content}
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
      }}>
      <div
        style={{
          minWidth: 1150,
          // width: 1065,
          height: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}>
        {content}
      </div>
    </div>
  );
};

export const dbTenderToTableEntry = (
  dbTender: DbTenderFolder,
  firstElementId?: string | number,
  isUnassigned?: boolean
): TableEntry => {
  const {
    id,
    name,
    description,
    parent_id,
    created_at,
    updated_at,
    state,
  } = dbTender;

  return {
    key: String(id),
    folder: true,
    moduleFile: false,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'blue',
      is_empty: false,
    },
    name: name || 'Unknown',
    description,
    notifications: Boolean(false),
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(parent_id),
    id,
    state,
    disabled: state !== DB_STATE.AVAILABLE,
    favourite: Boolean(false),
    isTenderDocuments: true,
    dbTenderFolder: dbTender,
    firstElementId,
    isUnassigned,
  };
};

export const dbFAttachmentToTableEntry = (
  dbAttachment: DbAttachment
): TableEntry => {
  const {
    id,
    name,
    title,
    created_at,
    state,
    updated_at,
    registration_id,
    description,
  } = dbAttachment;
  const nameSections = name ? name.split('.') : title && title.split('.');
  const extension =
    nameSections && nameSections.length
      ? nameSections[nameSections.length - 1].toLowerCase()
      : '';

  return {
    key: `file-${id}`,
    folder: false,
    moduleFile: false,
    type: (fileIcons[extension] || fileIcons.default) as typeof fileIcons.docx,
    extension,
    name: name || String(title),
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(registration_id),
    id: Number(id),
    state,
    description: description ? String(description) : '',
    disabled: state !== DB_STATE.AVAILABLE,
    dbAttachment,
    sourceType: 'tender-admin-root',
  };
};
